import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import {
  getLeaderboardProfileSummary,
  getLeaderboard, getPlayerRank,
  sendFriendRequest, acceptFriendRequest, declineFriendRequest, removeFriend,
} from './utils/supabaseActions';
import { useResponsive } from './hooks/useMediaQuery';
import { getStyles } from './styles';

// Lazy-load all screens and modals — splits initial bundle; each chunk downloaded on first visit
const loadCreateRoomScreen = () => import('./components/CreateRoomScreen');
const CreateRoomScreen = lazy(loadCreateRoomScreen);
const loadLobbyScreen = () => import('./components/LobbyScreen');
const LobbyScreen = lazy(loadLobbyScreen);
const loadLoginScreen = () => import('./components/LoginScreen');
const LoginScreen = lazy(loadLoginScreen);
const loadRoomScreen = () => import('./components/RoomScreen');
const RoomScreen = lazy(loadRoomScreen);
const loadGameScreen = () => import('./components/GameScreen');
const GameScreen = lazy(loadGameScreen);
const ProfileModal  = lazy(() => import('./components/ProfileModal'));
const SettingsModal = lazy(() => import('./components/SettingsModal'));
const HowToPlayModal = lazy(() => import('./components/HowToPlayModal'));
const ReplayModal   = lazy(() => import('./components/ReplayModal'));
const FeedbackModal = lazy(() => import('./components/FeedbackModal'));
const PeopleModal   = lazy(() => import('./components/PeopleModal'));
const SCREEN_PRELOADERS = Object.freeze({
  login: loadLoginScreen,
  'reset-password': loadLoginScreen,
  'verify-email': loadLoginScreen,
  'create-or-join': loadLobbyScreen,
  'create-room': loadCreateRoomScreen,
  room: loadRoomScreen,
  game: loadGameScreen,
});
const NEXT_SCREEN_PRELOADS = Object.freeze({
  login: ['create-or-join'],
  'reset-password': ['create-or-join'],
  'verify-email': ['create-or-join'],
  'create-or-join': ['create-room', 'room', 'game'],
  'create-room': ['create-or-join', 'room', 'game'],
  'rejoin-or-new': ['create-or-join', 'room', 'game'],
  room: ['create-or-join', 'game'],
  game: ['create-or-join'],
});
import { useToast } from './components/Toast';

// Custom hooks (extracted from App.js)
import { useSettings } from './hooks/useSettings';
import { useChat } from './hooks/useChat';
import { usePresence } from './hooks/usePresence';
import { useAIPlayer } from './hooks/useAIPlayer';
import { useAuth } from './hooks/useAuth';
import { useGameOrchestrator } from './hooks/useGameOrchestrator';
import { useRoomLobby } from './hooks/useRoomLobby';
import { resolveRoomSettings } from './utils/roomSettings';

export default function KosteczkiGame() {
  const { isMobile, isTablet, isLandscapeMobile } = useResponsive();
  const { addToast, ToastContainer } = useToast();

  //  Mobile sidebar 
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  //  Auth / nav state (owned here, setters passed to useAuth) 
  const [screen, setScreen] = useState('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupUsername, setSignupUsername] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [guestUsername, setGuestUsername] = useState('');

  //  Shared room identity (passed to useGameOrchestrator + useRoomLobby) 
  const [roomCode, setRoomCode] = useState('');
  const [roomData, setRoomData] = useState(null);
  const [isSpectator, setIsSpectator] = useState(false);
  const [isBotFight, setIsBotFight] = useState(false);
  const [rejoinableGames, setRejoinableGames] = useState([]);

  //  App-level config 
  const [numPlayers, setNumPlayers] = useState(6);
  const [botFightElo, setBotFightElo] = useState(1500);
  const [botFightNumBots, setBotFightNumBots] = useState(8);

  //  UI / leaderboard state 
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardTab, setLeaderboardTab] = useState('humans');
  const [currentUserRankInfo, setCurrentUserRankInfo] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [showMoveHistory, setShowMoveHistory] = useState(false);
  const [highlightedHistoryCell, setHighlightedHistoryCell] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [peopleModalAnchor, setPeopleModalAnchor] = useState(null);
  const [replayGameData, setReplayGameData] = useState(null);
  const [profileModalUser, setProfileModalUser] = useState(null);
  const [prevIsMyTurn, setPrevIsMyTurn] = useState(false);

  const titleFlashRef = useRef(null);
  const audioContextRef = useRef(null);

  //  Bridge refs 
  // gameActionsRef   lets useAIPlayer call the latest handleCellClick / swapMarkers
  //                   without adding them to its dep array.
  // authCallbacksRef  provides useAuth's logout() with stable callbacks that are
  //                    only available after usePresence / useSettings have run.
  // gameOrchestratorRef  lets useRoomLobby reset / seed game state without a
  //                       direct import dependency on useGameOrchestrator's state.
  // aiActionsRef     resolves circular call-order: useGameOrchestrator (needs
  //                   setLastExecutedAITurn) must be called before useAIPlayer
  //                   (which provides it), so we proxy through this ref.
  const gameActionsRef = useRef({});
  const authCallbacksRef = useRef({});
  const gameOrchestratorRef = useRef({});
  const aiActionsRef = useRef({ setLastExecutedAITurn: () => {}, onMoveDone: () => {} });

  // Stable wrappers so useGameOrchestrator dep arrays stay stable across renders
  const setLastExecutedAITurnStable = useCallback(
    (...args) => aiActionsRef.current.setLastExecutedAITurn(...args), []
  );
  const onMoveDoneStable = useCallback(
    () => aiActionsRef.current.onMoveDone(), []
  );

  //  useSettings 
  const {
    viewMode, setViewMode,
    preferredColorIndex, setPreferredColorIndex,
    showSymbols, setShowSymbols,
    showReactions, setShowReactions,
    chatSoundsEnabled, setChatSoundsEnabled,
    language, setLanguageAndPersist,
    themeKey, setThemeKeyAndPersist,
    t,
    loadSettingsFromProfile,
    finishInitializing,
    resetInitializing,
  } = useSettings(currentUser);
  const s = getStyles(isMobile, themeKey);
  const theme = s.activeTheme;

  useEffect(() => {
    const preloadTargets = NEXT_SCREEN_PRELOADS[screen] || [];

    preloadTargets.forEach((nextScreen) => {
      const loadScreen = SCREEN_PRELOADERS[nextScreen];
      if (!loadScreen) return;

      void loadScreen().catch((error) => {
        console.error(`Failed to preload ${nextScreen} screen:`, error);
      });
    });
  }, [screen]);

  const handleOpenCreateRoomScreen = useCallback(async () => {
    try {
      await loadCreateRoomScreen();
      setScreen('create-room');
    } catch (error) {
      console.error('Failed to load create room screen:', error);
    }
  }, []);

  //  useChat 
  const {
    messages, roomChatMessages,
    messageInput, setMessageInput,
    unreadChatCount, setUnreadChatCount,
    handleSendMessage,
    handleSendReaction,
    clearRoomChat,
    globalMessages,
    globalMessageInput, setGlobalMessageInput,
    globalUnreadCount, setGlobalUnreadCount,
    handleSendGlobalMessage,
    activeChatTab, setActiveChatTab,
    isChatWidgetOpen, setIsChatWidgetOpen,
  } = useChat(currentUser, roomCode, screen, addToast, t, chatSoundsEnabled);

  //  usePresence 
  const {
    onlinePlayers, setOnlinePlayers,
    onlinePlayersLoading, onlinePlayersError,
    friends, pendingRequests,
    friendsLoading, friendsError,
    showPeopleModal, setShowPeopleModal,
    peopleModalTab, setPeopleModalTab,
    loadFriendsData,
    clearPresence,
    refreshPresence,
  } = usePresence(currentUser, screen, roomCode, isSpectator);

  const openPeopleModal = useCallback((tab, e) => {
    setPeopleModalTab(tab);
    if (e?.currentTarget) {
      const r = e.currentTarget.getBoundingClientRect();
      setPeopleModalAnchor({ top: r.bottom + 6, left: r.left, right: window.innerWidth - r.right });
    } else {
      setPeopleModalAnchor(null);
    }
    setShowPeopleModal(true);
  }, [setPeopleModalTab, setShowPeopleModal]);

  //  loadLeaderboard  must be defined before useGameOrchestrator 
  const loadLeaderboard = useCallback(async () => {
    if (!currentUser) {
      setLeaderboard([]);
      setCurrentUserRankInfo(null);
      setCurrentUserProfile(null);
      return;
    }
    try {
      const includeBots = leaderboardTab === 'bots';
      const [data, rankInfo, profileSummary] = await Promise.all([
        getLeaderboard(includeBots),
        (!includeBots && currentUser) ? getPlayerRank(currentUser).catch(() => null) : Promise.resolve(null),
        getLeaderboardProfileSummary(currentUser).catch(() => null),
      ]);
      setLeaderboard(data);
      setCurrentUserRankInfo(rankInfo);
      if (profileSummary) setCurrentUserProfile(profileSummary);
    } catch (e) {
      console.error('Failed to load leaderboard:', e);
      setLeaderboard([]);
    }
  }, [currentUser, leaderboardTab]);

  //  useGameOrchestrator 
  const {
    gameBoard, setGameBoard,
    players, setPlayers,
    currentPlayer, setCurrentPlayer,
    selectedMarker, setSelectedMarker,
    draggedMarker,
    markersPool, setMarkersPool,
    moveHistory, setMoveHistory,
    gameEnded, setGameEnded,
    setEndGamePlayers,
    showGameEndModal, setShowGameEndModal,
    finalGamePlayers, setFinalGamePlayers,
    isConnectionOffline,
    isConnectionRecovering,
    gameEndingRef, gameLogRef, moveInProgressRef, gameStartTimeRef,
    handleGameEndModalClose,
    handleCellClick, handleDrop,
    handleDragStart, handleDragEnd, handleDragOver,
    swapMarkers,
  } = useGameOrchestrator({
    roomCode, roomData, isSpectator, isBotFight,
    setRoomCode, setRoomData, setIsSpectator,
    setRejoinableGames,
    screen, setScreen,
    currentUser,
    setLastExecutedAITurn: setLastExecutedAITurnStable,
    onMoveDone: onMoveDoneStable,
    loadLeaderboard,
    refreshPresence,
    clearRoomChat,
    addToast, t,
  });

  // Populate gameOrchestratorRef synchronously after hook runs (before any event)
  gameOrchestratorRef.current = {
    setGameBoard, setPlayers, setCurrentPlayer, setMarkersPool, setMoveHistory,
    setGameEnded, setShowGameEndModal, setFinalGamePlayers, setEndGamePlayers,
    gameEndingRef, gameLogRef, gameStartTimeRef,
  };

  // Wrap handleGameEndModalClose to also reset isBotFight when returning to lobby
  const handleGameEndModalCloseWrapped = useCallback(async () => {
    setIsBotFight(false);
    await handleGameEndModalClose();
  }, [handleGameEndModalClose]);

  //  useAIPlayer 
  // Called after useGameOrchestrator so moveInProgressRef is available.
  const {
    setLastExecutedAITurn,
    onMoveDone,
    handleUnstuckBot,
  } = useAIPlayer({
    screen, gameEnded, isSpectator, isBotFight,
    players, currentPlayer,
    gameBoard, markersPool, moveHistory,
    roomData, currentUser,
    moveInProgressRef,
    gameActionsRef,
  });

  // Fill aiActionsRef so the stable wrappers above delegate to the correct functions
  aiActionsRef.current.setLastExecutedAITurn = setLastExecutedAITurn;
  aiActionsRef.current.onMoveDone = onMoveDone;

  //  useRoomLobby 
  const {
    joinCodeInput, setJoinCodeInput,
    availableRooms, watchableRooms,
    roomListTab, setRoomListTab,
    mapAlgorithm, setMapAlgorithm,
    isPrivateRoom, setIsPrivateRoom,
    playerPreferredColors,
    isCreatingRoom, isJoiningRoom, isStartingGame, isAddingAI, readyCooldownUntil,
    handleCreateRoom, handleJoinRoom, handleQuickJoinRoom, handleQuickPlay,
    handleWatchRoom, handleStopWatching, handleRejoinGame,
    handleLeaveRoom, handleToggleReady, handleKickPlayer,
    handleAddAI, handleStartOnlineGame, handleRematch,
    handleCreateBotFight, isCreatingBotFight,
  } = useRoomLobby({
    roomCode, setRoomCode,
    roomData, setRoomData,
    isSpectator, isBotFight, setIsSpectator,
    setIsBotFight,
    setRejoinableGames,
    numPlayers, screen, setScreen,
    currentUser,
    setLastExecutedAITurn,
    gameOrchestratorRef,
    t, addToast, clearRoomChat,
  });

  const canSitInCurrentRoom = Boolean(
    roomData?.status === 'playing'
    && roomData?.players?.some((player) => player.username === currentUser && !player.isAI)
    && roomData?.game_state?.players?.some((player) => player.name === currentUser && player.isAI)
  );

  //  useAuth 
  // Last: its logout() reads authCallbacksRef which is populated below.
  const { login, loginAsGuest, upgradeGuestAccount, logout, handlePasswordReset, handleSetNewPassword } = useAuth({
    setCurrentUser,
    setScreen,
    loginEmail, loginPassword, signupUsername, isSignup, newPassword, guestUsername,
    setLoginEmail, setLoginPassword, setSignupUsername,
    setIsSignup, setIsResetPassword, setLoginError, setResetMessage, setNewPassword,
    setGuestUsername,
    setRejoinableGames,
    setIsGuest,
    loadSettingsFromProfile, finishInitializing, setLanguageAndPersist,
    language, themeKey,
    authCallbacksRef,
  });

  //  Effects 

  // Initial leaderboard load
  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  // NOTE: Session restore + onAuthStateChange subscription is handled by useAuth.

  // "It's Your Turn" Notification - Sound + Flashing Title
  useEffect(() => {
    const isMyTurn = screen === 'game' && players[currentPlayer]?.name === currentUser;

    if (isMyTurn && !prevIsMyTurn && screen === 'game') {
      console.log('TS YOUR TURN!');

      // iOS Safari: AudioContext must be created & resumed inside an async function
      // wrapped in try/catch — creating it outside a user gesture can throw
      // NotAllowedError, which would propagate out of useEffect and crash React
      // (white screen). resume() is async and must be awaited before using the context.
      const playTurnSound = async () => {
        try {
          if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          }
          const audioContext = audioContextRef.current;
          if (audioContext.state === 'suspended') {
            await audioContext.resume();
          }
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 600;
          oscillator.type = 'sine';
          gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
        } catch { /* iOS may block AudioContext outside user gesture — fail silently */ }
      };
      playTurnSound();

      if (!document.hasFocus()) {
        clearInterval(titleFlashRef.current);
        const originalTitle = document.title;
        let flashing = true;

        titleFlashRef.current = setInterval(() => {
          if (flashing) {
            document.title = t('titleYourTurn');
          } else {
            document.title = originalTitle;
          }
          flashing = !flashing;
        }, 600);

        const handleFocus = () => {
          clearInterval(titleFlashRef.current);
          document.title = originalTitle;
          window.removeEventListener('focus', handleFocus);
        };
        window.addEventListener('focus', handleFocus);
      }
    }

    setPrevIsMyTurn(isMyTurn);
  }, [currentPlayer, screen, currentUser, players, prevIsMyTurn, t]);

  // Refresh current user's leaderboard rank whenever they log in or out
  useEffect(() => {
    if (currentUser) {
      getPlayerRank(currentUser)
        .then(rankInfo => setCurrentUserRankInfo(rankInfo))
        .catch(() => setCurrentUserRankInfo(null));
    } else {
      setCurrentUserRankInfo(null);
    }
  }, [currentUser]);

  // UX-4: warn before the user accidentally navigates away from an active online game.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (screen === 'game' && !gameEnded && !isSpectator) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [screen, gameEnded, isSpectator]);

  //  Ref population (synchronous) 
  // gameActionsRef: allows useAIPlayer to call the latest handleCellClick / swapMarkers
  // without adding them to its dep array (which would restart the watchdog on every render).
  gameActionsRef.current.handleCellClick = handleCellClick;
  gameActionsRef.current.swapMarkers = swapMarkers;

  // authCallbacksRef: provides useAuth's logout() with the stable callbacks that are
  // only available after usePresence / useSettings have run (circular-dep resolution).
  authCallbacksRef.current.clearPresence = clearPresence;
  authCallbacksRef.current.resetInitializing = resetInitializing;
  authCallbacksRef.current.setOnlinePlayers = setOnlinePlayers;

  //  Profile modal handler 
  const handlePlayerClick = (username) => setProfileModalUser(username);

  //  Centralised header-modal renderer 
  const renderHeaderModals = () => (
    <Suspense fallback={null}>
      {landscapeBlocker}
      {showHowToPlay && <HowToPlayModal t={t} themeKey={themeKey} onClose={() => setShowHowToPlay(false)} />}
      {showSettings && <SettingsModal t={t} onClose={() => setShowSettings(false)} viewMode={viewMode} setViewMode={setViewMode} preferredColorIndex={preferredColorIndex} setPreferredColorIndex={setPreferredColorIndex} showSymbols={showSymbols} setShowSymbols={setShowSymbols} showReactions={showReactions} setShowReactions={setShowReactions} chatSoundsEnabled={chatSoundsEnabled} setChatSoundsEnabled={setChatSoundsEnabled} language={language} setLanguage={setLanguageAndPersist} themeKey={themeKey} setThemeKey={setThemeKeyAndPersist} isGuest={isGuest} upgradeGuestAccount={upgradeGuestAccount} />}
      {showFeedback && <FeedbackModal t={t} themeKey={themeKey} onClose={() => setShowFeedback(false)} currentUser={currentUser} />}
      {showPeopleModal && <PeopleModal t={t} themeKey={themeKey} friends={friends} pendingRequests={pendingRequests} onlinePlayers={onlinePlayers} onlinePlayersLoading={onlinePlayersLoading} onlinePlayersError={onlinePlayersError} friendsLoading={friendsLoading} friendsError={friendsError} currentUser={currentUser} initialTab={peopleModalTab} anchor={peopleModalAnchor} onClose={() => { setShowPeopleModal(false); setPeopleModalAnchor(null); }} onAccept={async (id) => { await acceptFriendRequest(id); loadFriendsData(); }} onDecline={async (id) => { await declineFriendRequest(id); loadFriendsData(); }} onRemove={async (id) => { await removeFriend(id); loadFriendsData(); }} onPlayerClick={(u) => { setShowPeopleModal(false); setPeopleModalAnchor(null); handlePlayerClick(u); }} onJoinFriend={(code) => { setShowPeopleModal(false); setPeopleModalAnchor(null); handleWatchRoom(code); }} />}
    </Suspense>
  );

  // LANDSCAPE BLOCKER — force portrait on mobile
  const landscapeBlocker = isLandscapeMobile ? (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: s.activeTheme.gradients.heroBackground,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: '20px', color: 'white', textAlign: 'center', padding: '24px',
    }}>
      <div style={{ fontSize: '56px' }}>📱</div>
      <div style={{ fontSize: '18px', fontWeight: 600, maxWidth: '300px' }}>
        {t('rotateToPotrait')}
      </div>
    </div>
  ) : null;
  const screenSuspenseFallback = (
    <div style={{
      ...s.container,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '12px' : '24px',
    }}>
      <div style={{
        ...s.card,
        width: 'min(420px, 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        textAlign: 'center',
      }}>
        <div style={{
          width: isMobile ? '42px' : '48px',
          height: isMobile ? '42px' : '48px',
          borderRadius: '16px',
          background: theme.gradients.buttonPrimary,
          boxShadow: `0 14px 28px ${theme.accents.soft}`,
        }} />
        <div style={{
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: '700',
          color: theme.text.body,
        }}>
          {t('loading')}
        </div>
      </div>
    </div>
  );

  // LOGIN SCREEN (reset-password, verify-email, login)
  if (screen === 'reset-password' || screen === 'verify-email' || screen === 'login') {
    return (
      <>
        {renderHeaderModals()}
        <Suspense fallback={screenSuspenseFallback}>
          <LoginScreen
            screen={screen}
            t={t}
            viewMode={viewMode}
            themeKey={themeKey}
            language={language}
            setLanguageAndPersist={setLanguageAndPersist}
            setShowHowToPlay={setShowHowToPlay}
            replayGameData={replayGameData}
            setReplayGameData={setReplayGameData}
            loginEmail={loginEmail}
            setLoginEmail={setLoginEmail}
            loginPassword={loginPassword}
            setLoginPassword={setLoginPassword}
            signupUsername={signupUsername}
            setSignupUsername={setSignupUsername}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            guestUsername={guestUsername}
            setGuestUsername={setGuestUsername}
            loginAsGuest={loginAsGuest}
            isSignup={isSignup}
            setIsSignup={setIsSignup}
            isResetPassword={isResetPassword}
            setIsResetPassword={setIsResetPassword}
            loginError={loginError}
            setLoginError={setLoginError}
            resetMessage={resetMessage}
            setResetMessage={setResetMessage}
            login={login}
            handlePasswordReset={handlePasswordReset}
            handleSetNewPassword={handleSetNewPassword}
            setScreen={setScreen}
            setCurrentUser={setCurrentUser}
          />
        </Suspense>
      </>
    );
  }

  // REJOIN OR NEW GAME SCREEN
  if (screen === 'rejoin-or-new') {
    const rejoinAccountButtonStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      flex: 1,
      minWidth: 0,
      minHeight: isMobile ? '44px' : '48px',
      padding: isMobile ? '10px 12px' : '12px 14px',
      borderRadius: '12px',
      border: `1px solid ${theme.borders.default}`,
      background: theme.surfaces.surface1,
      color: theme.text.body,
      cursor: 'pointer',
      boxShadow: 'none',
      textAlign: 'left',
    };
    const rejoinAccountBadgeStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '28px',
      padding: '0 10px',
      borderRadius: '999px',
      border: `1px solid ${isGuest ? theme.accents.softBorder : theme.borders.default}`,
      background: isGuest ? theme.accents.soft : theme.surfaces.surface2,
      color: isGuest ? theme.text.body : theme.text.muted,
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: '800',
      letterSpacing: '0.04em',
      flexShrink: 0,
      whiteSpace: 'nowrap',
    };
    const rejoinAccountBadgeText = isGuest ? t('createAccount') : t('profileLabel');
    const rejoinAccountButtonTitle = isGuest ? t('guestUpgrade') : t('profileLabel');
    const handleRejoinAccountClick = () => {
      if (isGuest) {
        setShowSettings(true);
        return;
      }
      setProfileModalUser(currentUser);
    };
    return (
      <>
        {renderHeaderModals()}
      <div style={s.container}>
        <ToastContainer themeKey={themeKey} />
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={s.card}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: isMobile ? '20px' : '32px', gap: '12px' }}>
                <button onClick={handleRejoinAccountClick} title={rejoinAccountButtonTitle} style={rejoinAccountButtonStyle}>
                  <span style={{ minWidth: 0, fontSize: isMobile ? '16px' : '24px', fontWeight: '800', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentUser}
                  </span>
                  <span style={rejoinAccountBadgeStyle}>{rejoinAccountBadgeText}</span>
                </button>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button onClick={(e) => openPeopleModal('online', e)} style={{ ...s.headerActionButton, background: theme.accents.infoSoft, border: `1px solid ${theme.accents.infoBorder}`, position: 'relative' }}>
                    {!isMobile && ` ${t('people')}`} ({onlinePlayers.length})
                    {pendingRequests.length > 0 && (
                      <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: theme.accents.danger, color: theme.palette.white, fontSize: '10px', fontWeight: '700', borderRadius: '8px', padding: '1px 5px', lineHeight: '14px', minWidth: '16px', textAlign: 'center' }}>{pendingRequests.length}</span>
                    )}
                  </button>
                  <button onClick={logout} style={s.compactButton}>
                     {t('logout')}
                  </button>
                </div>
              </div>

              {rejoinableGames.length > 0 && (
                <div style={{ marginBottom: isMobile ? '20px' : '32px' }}>
                  <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold', color: theme.accents.success, marginBottom: '16px' }}>
                    {t('activeGamesToRejoin')}
                  </h2>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                    {rejoinableGames.map((game, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: theme.accents.successSoft,
                          border: `2px solid ${theme.accents.successBorder}`,
                          borderRadius: '8px',
                          padding: isMobile ? '12px' : '16px',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          justifyContent: 'space-between',
                          alignItems: isMobile ? 'stretch' : 'center',
                          gap: isMobile ? '10px' : '0'
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <p style={{ color: theme.text.body, fontWeight: '600', fontSize: isMobile ? '14px' : '16px', margin: 0 }}>
                              {t('roomLabel').replace('{code}', game.code)}
                            </p>
                            <span style={{
                              fontSize: '10px', fontWeight: '800', letterSpacing: '0.06em', textTransform: 'uppercase',
                              padding: '1px 6px', borderRadius: '4px', verticalAlign: 'middle',
                              ...(game.status === 'waiting'
                                ? { color: theme.accents.warning, background: theme.accents.warningSoft, border: `1px solid ${theme.accents.warningBorder}` }
                                : { color: theme.accents.success, background: theme.accents.successSoft, border: `1px solid ${theme.accents.successBorder}` })
                            }}>
                              {game.status === 'waiting' ? t('roomStatusWaiting') : t('roomStatusPlaying')}
                            </span>
                          </div>
                          <p style={{ color: theme.text.muted, fontSize: '14px', margin: 0 }}>
                            {game.status === 'waiting'
                              ? t('playersInLobby').replace('{count}', game.players.length).replace('{total}', game.num_players)
                              : t('playersInGame').replace('{count}', game.players.length)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRejoinGame(game.code)}
                          style={{
                            ...s.greenButton,
                            width: isMobile ? '100%' : 'auto',
                            padding: '10px 20px'
                          }}
                        >
                          {game.status === 'waiting' ? t('returnToLobby') : t('rejoin')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ borderTop: `1px solid ${theme.borders.default}`, paddingTop: '24px' }}>
                <p style={{ color: theme.text.muted, fontSize: isMobile ? '13px' : '14px' }}>{t('rejoinOrLeaveHint')}</p>
              </div>
            </div>

        </div>
        <Suspense fallback={null}>
          {profileModalUser && (
            <ProfileModal t={t} username={profileModalUser} currentUser={currentUser} onClose={() => setProfileModalUser(null)} onReplay={setReplayGameData} onSendFriendRequest={async (from, to) => { await sendFriendRequest(from, to); loadFriendsData(); }} onAcceptFriend={async (id) => { await acceptFriendRequest(id); loadFriendsData(); }} onDeclineFriend={async (id) => { await declineFriendRequest(id); loadFriendsData(); }} onRemoveFriend={async (id) => { await removeFriend(id); loadFriendsData(); }} themeKey={themeKey} />
          )}
          {replayGameData && (
            <ReplayModal t={t} themeKey={themeKey} gameData={replayGameData} onClose={() => setReplayGameData(null)} showSymbols={showSymbols} viewMode={viewMode} />
          )}
        </Suspense>
      </div>
      </>
    );
  }

  // CREATE ROOM SCREEN
  if (screen === 'create-room') {
    return (
      <>
        {renderHeaderModals()}
        <Suspense fallback={screenSuspenseFallback}>
          <CreateRoomScreen
            t={t}
            themeKey={themeKey}
            currentUser={currentUser}
            numPlayers={numPlayers}
            setNumPlayers={setNumPlayers}
            mapAlgorithm={mapAlgorithm}
            setMapAlgorithm={setMapAlgorithm}
            isPrivateRoom={isPrivateRoom}
            setIsPrivateRoom={setIsPrivateRoom}
            handleCreateRoom={handleCreateRoom}
            isCreatingRoom={isCreatingRoom}
            isBotFight={isBotFight}
            setIsBotFight={setIsBotFight}
            botFightElo={botFightElo}
            setBotFightElo={setBotFightElo}
            botFightNumBots={botFightNumBots}
            setBotFightNumBots={setBotFightNumBots}
            handleCreateBotFight={handleCreateBotFight}
            isCreatingBotFight={isCreatingBotFight}
            setScreen={setScreen}
            onlinePlayers={onlinePlayers}
            onShowPeople={(e) => openPeopleModal('online', e)}
            pendingCount={pendingRequests.length}
            setShowSettings={setShowSettings}
            setShowHowToPlay={setShowHowToPlay}
            setShowFeedback={setShowFeedback}
            isGuest={isGuest}
            logout={logout}
            handlePlayerClick={handlePlayerClick}
            profileModalUser={profileModalUser}
            setProfileModalUser={setProfileModalUser}
          />
        </Suspense>
      </>
    );
  }

  // CREATE OR JOIN SCREEN (LOBBY)
  if (screen === 'create-or-join') {
    return (
      <>
        {renderHeaderModals()}
        <ToastContainer themeKey={themeKey} />
        <Suspense fallback={screenSuspenseFallback}>
          <LobbyScreen
            t={t}
            viewMode={viewMode}
            themeKey={themeKey}
            currentUser={currentUser}
            logout={logout}
            openCreateRoomScreen={handleOpenCreateRoomScreen}
            onShowPeople={(e) => openPeopleModal('online', e)}
            pendingRequests={pendingRequests}
            setShowSettings={setShowSettings}
            setShowHowToPlay={setShowHowToPlay}
            setShowFeedback={setShowFeedback}
            isGuest={isGuest}
            onlinePlayers={onlinePlayers}
            availableRooms={availableRooms}
            watchableRooms={watchableRooms}
            roomListTab={roomListTab}
            setRoomListTab={setRoomListTab}
            joinCodeInput={joinCodeInput}
            setJoinCodeInput={setJoinCodeInput}
            isJoiningRoom={isJoiningRoom}
            handleJoinRoom={handleJoinRoom}
            handleQuickJoinRoom={handleQuickJoinRoom}
            handleQuickPlay={handleQuickPlay}
            handleWatchRoom={handleWatchRoom}
            leaderboard={leaderboard}
            leaderboardTab={leaderboardTab}
            setLeaderboardTab={setLeaderboardTab}
            friends={friends}
            currentUserProfile={currentUserProfile}
            currentUserRankInfo={currentUserRankInfo}
            profileModalUser={profileModalUser}
            setProfileModalUser={setProfileModalUser}
            replayGameData={replayGameData}
            setReplayGameData={setReplayGameData}
            sendFriendRequest={sendFriendRequest}
            acceptFriendRequest={acceptFriendRequest}
            declineFriendRequest={declineFriendRequest}
            removeFriend={removeFriend}
            loadFriendsData={loadFriendsData}
            showMobileSidebar={showMobileSidebar}
            setShowMobileSidebar={setShowMobileSidebar}
            handlePlayerClick={handlePlayerClick}
            globalMessages={globalMessages}
            globalMessageInput={globalMessageInput}
            setGlobalMessageInput={setGlobalMessageInput}
            handleSendGlobalMessage={handleSendGlobalMessage}
            globalUnreadCount={globalUnreadCount}
            setGlobalUnreadCount={setGlobalUnreadCount}
            roomChatMessages={roomChatMessages}
            messageInput={messageInput}
            setMessageInput={setMessageInput}
            handleSendMessage={handleSendMessage}
            unreadChatCount={unreadChatCount}
            setUnreadChatCount={setUnreadChatCount}
            activeChatTab={activeChatTab}
            setActiveChatTab={setActiveChatTab}
            isChatWidgetOpen={isChatWidgetOpen}
            setIsChatWidgetOpen={setIsChatWidgetOpen}
            roomCode={roomCode}
          />
        </Suspense>
      </>
    );
  }

  // ROOM SCREEN
  if (screen === 'room' && roomData) {
    return (
      <>
        {renderHeaderModals()}
        <ToastContainer themeKey={themeKey} />
        <Suspense fallback={screenSuspenseFallback}>
          <RoomScreen
            t={t}
            viewMode={viewMode}
            themeKey={themeKey}
            roomData={roomData}
            roomCode={roomCode}
            currentUser={currentUser}
            onlinePlayers={onlinePlayers}
            pendingRequests={pendingRequests}
            playerPreferredColors={playerPreferredColors}
            currentUserRankInfo={currentUserRankInfo}
            onShowPeople={(e) => openPeopleModal('online', e)}
            setShowSettings={setShowSettings}
            setShowHowToPlay={setShowHowToPlay}
            setShowFeedback={setShowFeedback}
            isGuest={isGuest}
            profileModalUser={profileModalUser}
            setProfileModalUser={setProfileModalUser}
            replayGameData={replayGameData}
            setReplayGameData={setReplayGameData}
            handleToggleReady={handleToggleReady}
            handleAddAI={handleAddAI}
            handleStartOnlineGame={handleStartOnlineGame}
            handleLeaveRoom={handleLeaveRoom}
            handleKickPlayer={handleKickPlayer}
            isAddingAI={isAddingAI}
            isStartingGame={isStartingGame}
            readyCooldownUntil={readyCooldownUntil}
            sendFriendRequest={sendFriendRequest}
            acceptFriendRequest={acceptFriendRequest}
            declineFriendRequest={declineFriendRequest}
            removeFriend={removeFriend}
            loadFriendsData={loadFriendsData}
            globalMessages={globalMessages}
            globalMessageInput={globalMessageInput}
            setGlobalMessageInput={setGlobalMessageInput}
            handleSendGlobalMessage={handleSendGlobalMessage}
            globalUnreadCount={globalUnreadCount}
            setGlobalUnreadCount={setGlobalUnreadCount}
            roomChatMessages={roomChatMessages}
            messageInput={messageInput}
            setMessageInput={setMessageInput}
            handleSendMessage={handleSendMessage}
            unreadChatCount={unreadChatCount}
            setUnreadChatCount={setUnreadChatCount}
            activeChatTab={activeChatTab}
            setActiveChatTab={setActiveChatTab}
            isChatWidgetOpen={isChatWidgetOpen}
            setIsChatWidgetOpen={setIsChatWidgetOpen}
            handlePlayerClick={handlePlayerClick}
          />
        </Suspense>
      </>
    );
  }

  // GAME SCREEN
  if (screen === 'game') {
    const activeRoomSettings = resolveRoomSettings(roomData);

    return (
      <>
        {renderHeaderModals()}
        <Suspense fallback={screenSuspenseFallback}>
          <GameScreen
            t={t} isMobile={isMobile} isTablet={isTablet} ToastContainer={ToastContainer}
            gameBoard={gameBoard} players={players} currentPlayer={currentPlayer}
            moveHistory={moveHistory} markersPool={markersPool}
            aiControlledMoveCounts={roomData?.game_state?.aiControlledMoveCounts ?? {}}
            aiControlledMoveHistory={Array.isArray(roomData?.game_state?.moves) ? roomData.game_state.moves : moveHistory}
            currentUser={currentUser} roomCode={roomCode} isSpectator={isSpectator || isBotFight}
            viewMode={viewMode} themeKey={themeKey} showSymbols={showSymbols} showReactions={showReactions}
            selectedMarker={selectedMarker} setSelectedMarker={setSelectedMarker}
            draggedMarker={draggedMarker}
            hoveredCell={hoveredCell} setHoveredCell={setHoveredCell}
            showMoveHistory={showMoveHistory} setShowMoveHistory={setShowMoveHistory}
            highlightedHistoryCell={highlightedHistoryCell} setHighlightedHistoryCell={setHighlightedHistoryCell}
            handleCellClick={handleCellClick}
            handleDragStart={handleDragStart} handleDragEnd={handleDragEnd}
            handleDragOver={handleDragOver} handleDrop={handleDrop}
            swapMarkers={swapMarkers}
            handleLeaveRoom={handleLeaveRoom} handleStopWatching={handleStopWatching}
            handleSendReaction={handleSendReaction} messages={messages}
            onlinePlayers={onlinePlayers} pendingRequests={pendingRequests}
            onShowPeople={(e) => openPeopleModal('online', e)}
            setShowSettings={setShowSettings} setShowHowToPlay={setShowHowToPlay} setShowFeedback={setShowFeedback}
            isGuest={isGuest}
            profileModalUser={profileModalUser} setProfileModalUser={setProfileModalUser}
            showGameEndModal={showGameEndModal} finalGamePlayers={finalGamePlayers} handleGameEndModalClose={handleGameEndModalCloseWrapped}
            handleRematch={handleRematch}
            gameStartTime={gameStartTimeRef.current}
            gameLog={gameLogRef.current}
            isConnectionOffline={isConnectionOffline}
            isConnectionRecovering={isConnectionRecovering}
            replayGameData={replayGameData} setReplayGameData={setReplayGameData}
            sendFriendRequest={sendFriendRequest} acceptFriendRequest={acceptFriendRequest}
            declineFriendRequest={declineFriendRequest} removeFriend={removeFriend} loadFriendsData={loadFriendsData}
            globalMessages={globalMessages} globalMessageInput={globalMessageInput} setGlobalMessageInput={setGlobalMessageInput}
            handleSendGlobalMessage={handleSendGlobalMessage}
            globalUnreadCount={globalUnreadCount} setGlobalUnreadCount={setGlobalUnreadCount}
            roomChatMessages={roomChatMessages} messageInput={messageInput} setMessageInput={setMessageInput}
            handleSendMessage={handleSendMessage}
            unreadChatCount={unreadChatCount} setUnreadChatCount={setUnreadChatCount}
            activeChatTab={activeChatTab} setActiveChatTab={setActiveChatTab}
            isChatWidgetOpen={isChatWidgetOpen} setIsChatWidgetOpen={setIsChatWidgetOpen}
            swapLimitPerPlayer={activeRoomSettings.swapLimitPerPlayer}
            turnTimerSeconds={activeRoomSettings.turnTimerSeconds}
            turnDeadlineAt={roomData?.game_state?.turnDeadlineAt ?? null}
            onSitIn={canSitInCurrentRoom ? () => handleRejoinGame(roomCode) : null}
            onUnstuckBot={currentUser === roomData?.host ? handleUnstuckBot : null}
          />
        </Suspense>
      </>
    );
  }

  return null;
}
